/**
 * OSS 分片直传 + 断点续传核心（立方镜 2026-09-17）
 *
 * ⚠️ 纯 JS，不依赖 uni / axios / 浏览器 API，App(imym/uniapp-立方镜-荣耀/common/ossMultipart.js) 与 PC(新版代码/立方镜/box-im/im-web/src/api/ossMultipart.js) 两处内容完全相同，改一处要同步另一处。
 * 平台相关的事（发请求、读分片、存进度）由调用方通过 adapter 注入。
 *
 * 流程（文件字节全部客户端直连 OSS，服务端只发 STS 临时凭证）：
 *   1. 向本台 /oss/sts 要凭证：新上传由服务端生成 key；续传带上次的 key
 *   2. InitiateMultipartUpload 拿 uploadId（续传时复用本地记录的）
 *   3. ListParts 查已传分片 → 只传缺的，并发 concurrency 片，单片失败退避重试，凭证快过期自动换
 *   4. ListParts 取全部 ETag → CompleteMultipartUpload（x-oss-forbid-overwrite，防止覆盖已有文件）
 *
 * 签名：OSS V1（HMAC-SHA1）。浏览器不能设置 Date 头，统一用 x-oss-date（StringToSign 的 Date 行填同一个值）。
 *
 * adapter = {
 *   getSts(key)                          → Promise<UploadStsVO|null>   key 为空表示新上传
 *   request({method,url,headers,body})   → Promise<{status, data(string)}>  body: ArrayBuffer|string|undefined
 *   readChunk(start, end)                → Promise<ArrayBuffer>
 *   loadCheckpoint() / saveCheckpoint(cp) / clearCheckpoint()
 *   log(step, data)                      → 可选，上传日志（关键节点+错误），不传则不记录
 * }
 */

// ---------------- SHA1 / HMAC / base64 ----------------

const utf8Bytes = (str) => {
	const out = [];
	for (let i = 0; i < str.length; i++) {
		let c = str.charCodeAt(i);
		if (c >= 0xd800 && c <= 0xdbff && i + 1 < str.length) {
			c = 0x10000 + ((c - 0xd800) << 10) + (str.charCodeAt(++i) - 0xdc00);
		}
		if (c < 0x80) {
			out.push(c);
		} else if (c < 0x800) {
			out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
		} else if (c < 0x10000) {
			out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
		} else {
			out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
		}
	}
	return out;
}

const sha1 = (bytes) => {
	const ml = bytes.length;
	const withPadding = bytes.slice();
	withPadding.push(0x80);
	while (withPadding.length % 64 !== 56) {
		withPadding.push(0);
	}
	const bitLenHi = Math.floor(ml * 8 / 0x100000000);
	const bitLenLo = (ml * 8) >>> 0;
	withPadding.push((bitLenHi >>> 24) & 255, (bitLenHi >>> 16) & 255, (bitLenHi >>> 8) & 255, bitLenHi & 255);
	withPadding.push((bitLenLo >>> 24) & 255, (bitLenLo >>> 16) & 255, (bitLenLo >>> 8) & 255, bitLenLo & 255);
	let h0 = 0x67452301, h1 = 0xefcdab89, h2 = 0x98badcfe, h3 = 0x10325476, h4 = 0xc3d2e1f0;
	const w = new Array(80);
	for (let off = 0; off < withPadding.length; off += 64) {
		for (let i = 0; i < 16; i++) {
			w[i] = (withPadding[off + i * 4] << 24) | (withPadding[off + i * 4 + 1] << 16) |
				(withPadding[off + i * 4 + 2] << 8) | withPadding[off + i * 4 + 3];
		}
		for (let i = 16; i < 80; i++) {
			const t = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
			w[i] = (t << 1) | (t >>> 31);
		}
		let a = h0, b = h1, c = h2, d = h3, e = h4;
		for (let i = 0; i < 80; i++) {
			let f, k;
			if (i < 20) {
				f = (b & c) | (~b & d);
				k = 0x5a827999;
			} else if (i < 40) {
				f = b ^ c ^ d;
				k = 0x6ed9eba1;
			} else if (i < 60) {
				f = (b & c) | (b & d) | (c & d);
				k = 0x8f1bbcdc;
			} else {
				f = b ^ c ^ d;
				k = 0xca62c1d6;
			}
			const temp = (((a << 5) | (a >>> 27)) + f + e + k + w[i]) | 0;
			e = d;
			d = c;
			c = (b << 30) | (b >>> 2);
			b = a;
			a = temp;
		}
		h0 = (h0 + a) | 0;
		h1 = (h1 + b) | 0;
		h2 = (h2 + c) | 0;
		h3 = (h3 + d) | 0;
		h4 = (h4 + e) | 0;
	}
	const out = [];
	[h0, h1, h2, h3, h4].forEach(h => out.push((h >>> 24) & 255, (h >>> 16) & 255, (h >>> 8) & 255, h & 255));
	return out;
}

const hmacSha1 = (keyStr, msgStr) => {
	let key = utf8Bytes(keyStr);
	if (key.length > 64) {
		key = sha1(key);
	}
	while (key.length < 64) {
		key.push(0);
	}
	const ipad = key.map(b => b ^ 0x36);
	const opad = key.map(b => b ^ 0x5c);
	return sha1(opad.concat(sha1(ipad.concat(utf8Bytes(msgStr)))));
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const base64 = (bytes) => {
	let out = '';
	for (let i = 0; i < bytes.length; i += 3) {
		const n = (bytes[i] << 16) | ((bytes[i + 1] || 0) << 8) | (bytes[i + 2] || 0);
		out += B64[(n >> 18) & 63] + B64[(n >> 12) & 63];
		out += i + 1 < bytes.length ? B64[(n >> 6) & 63] : '=';
		out += i + 2 < bytes.length ? B64[n & 63] : '=';
	}
	return out;
}

// ---------------- OSS 请求 ----------------

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => (n < 10 ? '0' : '') + n;
const gmtDate = () => {
	// 不用 toUTCString：个别 JS 引擎格式不一致（如单位数日期不补零）
	const d = new Date();
	return DAYS[d.getUTCDay()] + ', ' + pad2(d.getUTCDate()) + ' ' + MONTHS[d.getUTCMonth()] + ' ' +
		d.getUTCFullYear() + ' ' + pad2(d.getUTCHours()) + ':' + pad2(d.getUTCMinutes()) + ':' +
		pad2(d.getUTCSeconds()) + ' GMT';
}

/**
 * 发一个带 V1 签名的 OSS 请求
 * @param subResources 如 {partNumber: 1, uploadId: 'xx'}，值为 null 表示无值（如 uploads）
 */
const ossRequest = async (adapter, sts, method, subResources, contentType, extraHeaders, body) => {
	const date = gmtDate();
	const headers = Object.assign({
		'Content-Type': contentType,
		'x-oss-date': date
	}, extraHeaders || {});
	if (sts.securityToken) {
		headers['x-oss-security-token'] = sts.securityToken;
	}
	const ossHeaders = Object.keys(headers)
		.filter(k => k.toLowerCase().indexOf('x-oss-') === 0)
		.map(k => k.toLowerCase() + ':' + String(headers[k]).trim())
		.sort()
		.join('\n');
	const query = Object.keys(subResources || {}).sort()
		.map(k => subResources[k] === null ? k : k + '=' + subResources[k])
		.join('&');
	const resource = '/' + sts.bucket + '/' + sts.key + (query ? '?' + query : '');
	// 浏览器设不了 Date 头，改发 x-oss-date；Date 行也必须填同一个值（实测留空会 SignatureDoesNotMatch，
	// OSS 返回的 StringToSign 里 Date 行就是 x-oss-date 的值）
	const stringToSign = method + '\n\n' + contentType + '\n' + date + '\n' + ossHeaders + '\n' + resource;
	headers['Authorization'] = 'OSS ' + sts.accessKeyId + ':' + base64(hmacSha1(sts.accessKeySecret, stringToSign));
	return adapter.request({
		method,
		url: sts.host + '/' + sts.key + (query ? '?' + query : ''),
		headers,
		body
	});
}

const xmlValue = (xml, tag) => {
	const m = new RegExp('<' + tag + '>([\\s\\S]*?)</' + tag + '>').exec(xml || '');
	return m ? m[1] : null;
}

const ossError = (res) => {
	const err = new Error('OSS ' + res.status + ' ' + (xmlValue(res.data, 'Code') || ''));
	err.status = res.status;
	err.code = xmlValue(res.data, 'Code');
	return err;
}

const VIDEO_TYPES = { mp4: 'video/mp4', mov: 'video/quicktime', m4v: 'video/x-m4v', '3gp': 'video/3gpp', webm: 'video/webm' };
const contentTypeOf = (key) => VIDEO_TYPES[key.split('.').pop().toLowerCase()] || 'application/octet-stream';

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// 凭证剩余不足 5 分钟就换新的
const stsExpiring = (sts) => {
	const t = Date.parse(sts.expiration);
	return !t || t - Date.now() < 5 * 60 * 1000;
}

/**
 * @param options {size, adapter, concurrency=3, onProgress(loaded,total)}
 * @returns UploadStsVO（含 url / coverUrl）；本台不支持分片直传返回 null；失败抛异常
 */
const multipartUpload = async (options) => {
	const { size, adapter } = options;
	const concurrency = options.concurrency || 3;
	const onProgress = options.onProgress || (() => {});
	const log = (step, data) => {
		try {
			adapter.log && adapter.log(step, data);
		} catch (e) {
			// 埋点失败不影响上传
		}
	};

	let cp = adapter.loadCheckpoint();
	if (cp && cp.size !== size) {
		cp = null;
	}
	// getSts：服务端明确拒绝返回 null；网络失败直接抛错（续传记录保留，下次接着传，不会因为网络抖一下就从头传）
	let sts = await adapter.getSts(cp ? cp.key : null);
	if (!sts && cp) {
		// 续传的 key 被服务端拒绝（格式不对/不是本人签发/记录过期等），当新上传处理
		log('resume_rejected');
		adapter.clearCheckpoint();
		cp = null;
		sts = await adapter.getSts(null);
	}
	log('sts', { ok: !!(sts && sts.enable), completed: !!(sts && sts.completed), resume: !!cp });
	if (!sts || !sts.enable) {
		return null;
	}
	if (sts.completed) {
		adapter.clearCheckpoint();
		return sts;
	}
	const refreshSts = async () => {
		if (stsExpiring(sts)) {
			const fresh = await adapter.getSts(sts.key);
			if (fresh && fresh.enable && !fresh.completed) {
				sts = fresh;
			}
		}
	}

	const listParts = async () => {
		await refreshSts();
		const res = await ossRequest(adapter, sts, 'GET', { uploadId: cp.uploadId }, 'application/xml');
		if (res.status !== 200) {
			throw ossError(res);
		}
		const parts = {};
		const re = /<Part>([\s\S]*?)<\/Part>/g;
		let m;
		while ((m = re.exec(res.data))) {
			parts[Number(xmlValue(m[1], 'PartNumber'))] = {
				etag: xmlValue(m[1], 'ETag'),
				size: Number(xmlValue(m[1], 'Size'))
			};
		}
		return parts;
	}

	const initiate = async () => {
		const res = await ossRequest(adapter, sts, 'POST', { uploads: null }, contentTypeOf(sts.key),
			{ 'x-oss-forbid-overwrite': 'true' });
		if (res.status !== 200) {
			throw ossError(res);
		}
		cp = { key: sts.key, uploadId: xmlValue(res.data, 'UploadId'), partSize: sts.partSize, size: size };
		adapter.saveCheckpoint(cp);
		log('init_ok');
	}

	if (!cp || cp.key !== sts.key) {
		await initiate();
	}
	let done;
	try {
		done = await listParts();
	} catch (e) {
		if (e.code !== 'NoSuchUpload') {
			throw e;
		}
		// 本地记录的 uploadId 在 OSS 上已失效，重新发起
		await initiate();
		done = {};
	}

	const partSize = cp.partSize;
	const partCount = Math.max(1, Math.ceil(size / partSize));
	const partLength = (n) => Math.min(partSize, size - (n - 1) * partSize);
	let loaded = 0;
	const todo = [];
	for (let n = 1; n <= partCount; n++) {
		if (done[n] && done[n].size === partLength(n)) {
			loaded += partLength(n);
		} else {
			todo.push(n);
		}
	}
	onProgress(loaded, size);
	log('parts_todo', { total: partCount, todo: todo.length, partSize: partSize });

	const uploadPart = async (n) => {
		const start = (n - 1) * partSize;
		const data = await adapter.readChunk(start, start + partLength(n));
		// 读出来的长度必须与分片长度一致，否则合并出的文件是坏的（2026-09-17 plus.io slice 多读 1 字节导致视频 0:00）
		if (!data || data.byteLength !== partLength(n)) {
			throw new Error('读取分片长度异常 part=' + n + ' 期望=' + partLength(n) + ' 实际=' + (data && data.byteLength));
		}
		// 弱网/切网络时退避重试，最多约 1 分钟
		for (let attempt = 0;; attempt++) {
			try {
				await refreshSts();
				const res = await ossRequest(adapter, sts, 'PUT', { partNumber: n, uploadId: cp.uploadId },
					'application/octet-stream', null, data);
				if (res.status === 200) {
					return;
				}
				throw ossError(res);
			} catch (e) {
				// 4xx（签名/权限/uploadId 失效）重试也没用，直接失败；凭证过期除外
				const fatal = e.status >= 400 && e.status < 500 && e.code !== 'SecurityTokenExpired' &&
					e.code !== 'RequestTimeTooSkewed';
				log('part_err', { n: n, attempt: attempt, status: e.status, code: e.code, msg: String(e.message || e.errMsg || e).slice(0, 80) });
				if (fatal || attempt >= 6) {
					throw e;
				}
				if (e.code === 'SecurityTokenExpired') {
					sts.expiration = '';
				}
				await sleep(Math.min(1000 * Math.pow(2, attempt), 10000));
			}
		}
	}

	let next = 0;
	let failed = null;
	const worker = async () => {
		while (!failed && next < todo.length) {
			const n = todo[next++];
			try {
				await uploadPart(n);
				loaded += partLength(n);
				onProgress(loaded, size);
			} catch (e) {
				failed = failed || e;
			}
		}
	}
	const workers = [];
	for (let i = 0; i < Math.min(concurrency, todo.length); i++) {
		workers.push(worker());
	}
	await Promise.all(workers);
	log('parts_done', { failed: !!failed });
	if (failed) {
		// 保留续传记录，下次同一个文件接着传
		throw failed;
	}

	// 以 OSS 上实际存在的分片为准合并
	const parts = await listParts();
	let xml = '<CompleteMultipartUpload>';
	for (let n = 1; n <= partCount; n++) {
		if (!parts[n]) {
			throw new Error('OSS 分片缺失 ' + n);
		}
		xml += '<Part><PartNumber>' + n + '</PartNumber><ETag>' + parts[n].etag + '</ETag></Part>';
	}
	xml += '</CompleteMultipartUpload>';
	const res = await ossRequest(adapter, sts, 'POST', { uploadId: cp.uploadId }, 'application/xml',
		{ 'x-oss-forbid-overwrite': 'true' }, xml);
	log('complete_res', { status: res.status, code: xmlValue(res.data, 'Code') });
	if (res.status !== 200) {
		throw ossError(res);
	}
	adapter.clearCheckpoint();
	return sts;
}

const FP_SAMPLE = 64 * 1024;

/**
 * 文件指纹 = 大小 + 头尾各 64KB 的 SHA1，用来认「是不是同一个文件」以便续传。
 * 不能用临时路径：App 选视频开了系统压缩，同一个视频每次选都会生成新的临时文件。
 */
const fileFingerprint = async (size, readChunk) => {
	const head = new Uint8Array(await readChunk(0, Math.min(FP_SAMPLE, size)));
	const tailStart = Math.max(0, size - FP_SAMPLE);
	const tail = new Uint8Array(await readChunk(tailStart, size));
	const bytes = Array.prototype.slice.call(head).concat(Array.prototype.slice.call(tail));
	return size + '_' + sha1(bytes).map(b => (b < 16 ? '0' : '') + b.toString(16)).join('');
}

export {
	multipartUpload,
	fileFingerprint
}
